<template>
  <div class="booking-content">
    <!-- 중앙: 클래스 예약 -->
    <div class="panel" aria-label="클래스 예약">
      <div class="panel-head">
        <div class="panel-title-row">
          <h2>클래스 예약</h2>
          <div class="panel-actions">
            <!-- CAR-130 날짜 닫기(2026-10-24 기본값 D2, 휴무일): 고른 날짜의 수업을 모두 지운다. 확인 창에서 그 날짜 수업을 보인다 -->
            <button
              class="close-date-btn"
              type="button"
              name="close-date"
              :disabled="!canCloseDate"
              @click="openCloseDateModal"
            >
              이 날짜 수업 닫기
            </button>
            <!-- R9 수업 추가(record-daily-design.md 0절 7·1.2): 날짜·타입(9개)·시작/종료 시각·정원 -->
            <button
              class="add-class-btn"
              type="button"
              :disabled="classSaving"
              @click="openAddClassModal"
            >
              + 수업 추가
            </button>
          </div>
        </div>
        <p
          v-if="scheduleNotice"
          class="schedule-notice"
          :class="{ 'schedule-notice--error': scheduleNoticeError }"
          role="status"
        >
          {{ scheduleNotice }}
        </p>

        <!-- 날짜 선택 -->
        <div class="week">
          <button
            v-for="(d, i) in days"
            :key="d.date"
            :class="['day', { sel: i === selectedIdx }]"
            @click="selectDay(i)"
          >
            <span class="dow">{{ d.dow }}</span>
            <span class="num">{{ d.num }}</span>
          </button>
        </div>

        <!-- 주차 네비게이션 버튼 -->
        <div class="week-navigation">
          <button
            class="nav-btn prev-week"
            @click="goToPreviousWeek"
            :disabled="loading"
            title="이전 주"
          >
            ← 이전 주
          </button>
          <span class="current-week">{{ getCurrentWeekText() }}</span>
          <button
            class="nav-btn next-week"
            @click="goToNextWeek"
            :disabled="loading"
            title="다음 주"
          >
            다음 주 →
          </button>
        </div>
      </div>

      <!-- 스케줄 리스트 -->
      <div class="rows">
        <ScheduleRow
          v-for="cls in schedule"
          :key="cls.id"
          :time="cls.time"
          :type="cls.type"
          :booked="cls.booked"
          :capacity="cls.capacity"
          :status="cls.status || ''"
          :mine="cls.mine || false"
          :selected="selectedClass && selectedClass.id === cls.id"
          :type-label="cls.type_label || ''"
          :class-title="cls.title || ''"
          @book="addMember(cls)"
          @free-trial="openFreeTrialForClass(cls)"
          @click="selectClass(cls)"
        />
        <p v-if="!loading && schedule.length === 0" class="empty">
          해당 날짜의 수업이 없습니다.
        </p>
        <p v-if="loading" class="empty">불러오는 중…</p>
      </div>
    </div>

    <!-- 우측: 수업 예약자 목록 -->
    <aside class="right" aria-label="수업 예약자">
      <div class="my-panel">
        <div class="my-panel-header">
          <h3 class="panel-title">
            {{
              selectedClass
                ? `${hhmm(selectedClass.time)} ${classNameOf(selectedClass)}`
                : "수업 선택"
            }}
          </h3>
          <div v-if="selectedClass" class="class-info">
            <span class="capacity"
              >{{ selectedClass.booked }} / {{ selectedClass.capacity }}명</span
            >
          </div>
        </div>

        <!-- R9 수업 관리(record-daily-design.md 0절 9·10): 예약이나 운동 기록이 있으면 수정·삭제 못 함(서버 409와 같게).
             R5의 "수업 정보"(부위·포커스) 상자는 뺐다(0절 12·13: 수업에는 타입만, 포커스는 운동 프로그램 탭) -->
        <section
          v-if="selectedClass"
          class="class-manage"
          aria-label="수업 관리"
        >
          <div class="class-manage__head">
            <h4 class="class-manage__title">수업 관리</h4>
            <span class="class-manage__date"
              >{{ selectedClass.date }} {{ hhmm(selectedClass.time) }}~{{
                hhmm(selectedClass.end_time)
              }}</span
            >
          </div>
          <div class="class-manage__actions">
            <button
              class="class-manage-btn"
              type="button"
              name="class-edit"
              :disabled="!lock.editable || classBusy"
              @click="openEditClassModal"
            >
              수업 수정
            </button>
            <button
              class="class-manage-btn class-manage-btn--danger"
              type="button"
              name="class-delete"
              :disabled="!lock.deletable || classBusy"
              @click="confirmDeleteClass"
            >
              {{ deleting ? "삭제 중…" : "수업 삭제" }}
            </button>
            <router-link
              class="class-manage__program"
              :to="programLink(selectedClass)"
              >운동 프로그램 →</router-link
            >
          </div>
          <p v-if="lock.reason" class="class-manage__hint">
            {{ lock.reason }}
          </p>
          <p v-else-if="bookingsLoadFailed" class="class-manage__hint">
            예약자 목록을 불러오지 못해 수정·삭제를 막아 뒀어요. 수업을 다시
            골라 주세요.
          </p>
          <p v-else-if="!lockKnown" class="class-manage__hint">
            예약을 확인하는 중…
          </p>
          <p
            v-if="manageMessage"
            class="class-manage__msg"
            :class="{ 'class-manage__msg--error': manageError }"
            role="status"
          >
            {{ manageMessage }}
          </p>
        </section>

        <div class="my-panel-content">
          <div v-if="!selectedClass" class="my-empty">수업을 선택해주세요.</div>

          <div v-else-if="loadingBookings" class="my-empty">
            예약자 목록을 불러오는 중...
          </div>

          <div v-else-if="classBookings.length === 0" class="my-empty">
            예약자가 없습니다.
          </div>

          <div v-else>
            <div
              v-for="booking in classBookings"
              :key="booking.id"
              class="my-item"
            >
              <div class="my-item-content">
                <div class="member-info">
                  <!-- 게스트(무료체험·드랍인) 이름도 슬롯 계정 화면으로 간다: 코치가 그 계정의 운동기록에서 게스트 기록을 실제 회원에게
                       이관한다. 체험 상담은 오른쪽 "체험 기록" 링크(booking_id)로 따로 연다(1fc7d5f4에서 이름 이동을 막아 이관 경로가 끊겼다) -->
                  <div
                    class="member-name clickable"
                    @click="goToMemberProfile(booking.user_id)"
                  >
                    {{ booking.guest_name || booking.user_name }}
                    <span v-if="booking.guest_name" class="guest-tag">{{
                      booking.user_name
                    }}</span>
                  </div>
                  <div v-if="booking.guest_phone" class="member-sub">
                    {{ booking.guest_phone }}
                  </div>

                  <!-- 회원권·출석 이력은 실회원만. 게스트(무료체험)는 슬롯 계정 값이 섞여 뜻이 없다 -->
                  <div v-if="!booking.guest_name" class="member-badges">
                    <span
                      class="badge"
                      :class="membershipBadgeClass(booking)"
                      >{{ membershipText(booking) }}</span
                    >
                    <span class="badge" :class="visitBadgeClass(booking)">{{
                      visitText(booking)
                    }}</span>
                  </div>

                  <!-- 특이사항(users.admin_note): 부상·주의사항이라 눈에 걸려야 한다 -->
                  <p v-if="booking.admin_note" class="member-note">
                    <span class="note-mark" aria-hidden="true">!</span>
                    {{ booking.admin_note }}
                  </p>

                  <div v-if="booking.max_hr" class="member-max-hr">
                    최대심박수: {{ booking.max_hr }} bpm
                  </div>
                </div>
                <div class="booking-actions">
                  <!-- 게스트 전화번호로 신청을 추정하지 않고 booking_id로 정확한 체험 건을 연다. -->
                  <router-link
                    v-if="booking.guest_name"
                    class="trial-record-link"
                    :to="trialRecordLink(booking)"
                  >
                    체험 기록
                  </router-link>
                  <span
                    class="attendance-status-chip"
                    :class="getAttendanceStatusClass(booking.attended)"
                  >
                    {{ getAttendanceStatusText(booking.attended) }}
                  </span>
                  <button
                    v-if="!booking.attended && booking.status !== 'cancelled'"
                    class="attendance-btn"
                    @click="markAttendance(booking.id)"
                    title="출석 처리"
                  >
                    출석하기
                  </button>
                  <button
                    class="cancel-booking-btn"
                    @click="cancelBooking(booking)"
                    :disabled="booking.status === 'cancelled'"
                    title="예약 취소"
                  >
                    예약취소
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>

    <!-- 게스트 예약 취소 사유 모달 -->
    <div
      v-if="showCancelReasonModal"
      class="modal-overlay"
      @click="showCancelReasonModal = false"
    >
      <div class="modal-container" @click.stop>
        <h3 class="modal-title">무료체험 예약 취소</h3>
        <p class="modal-message">
          {{ cancelTarget?.guest_name }}님의 무료체험 예약을 취소합니다.<br />
          취소 사유를 선택해주세요.
        </p>
        <p class="modal-footnote">
          관리자 정정은 기존 문의에 연결한 예약이면 체험·상담 기록은 보존하고
          예약 연결만 해제합니다.
        </p>
        <div class="modal-buttons">
          <button
            class="modal-btn modal-btn-confirm"
            @click="doCancelBooking('customer')"
          >
            고객 취소 (이력에 '예약취소'로 남김)
          </button>
          <button
            class="modal-btn modal-btn-cancel"
            @click="doCancelBooking('correction')"
          >
            관리자 정정 (잘못 등록된 예약 연결 해제)
          </button>
          <button
            class="modal-btn modal-btn-cancel"
            @click="showCancelReasonModal = false"
          >
            닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 무료체험 추가 모달 -->
    <div
      v-if="showFreeTrialModal"
      class="modal-overlay"
      @click="closeFreeTrialModal"
    >
      <div class="modal-container modal-container--form" @click.stop>
        <h3 class="modal-title">무료체험·드랍인 추가</h3>
        <p class="modal-message">
          {{ hhmm(selectedClass?.time) }}
          {{ classNameOf(selectedClass) }} 수업에
          {{ visitKindLabel(freeTrialForm.visitKind) }} 손님을 추가합니다.
        </p>
        <p v-if="freeTrialForm.requestId" class="trial-link-note">
          체험 고객 관리의 기존 문의를 이 예약에 연결합니다.
        </p>
        <div class="modal-form">
          <div class="visit-kind" role="radiogroup" aria-label="방문 종류">
            <label
              v-for="k in visitKinds"
              :key="k.value"
              :class="[
                'visit-kind-opt',
                { active: freeTrialForm.visitKind === k.value },
              ]"
            >
              <input
                type="radio"
                :value="k.value"
                :checked="freeTrialForm.visitKind === k.value"
                @change="pickVisitKind(k.value)"
              />
              {{ k.label }}
            </label>
          </div>
          <UiInput
            v-model="freeTrialForm.name"
            placeholder="이름"
            type="text"
          />
          <UiInput
            v-model="freeTrialForm.phone"
            placeholder="전화번호 (01012345678)"
            type="tel"
            @update:modelValue="onFreeTrialPhone"
          />
          <p v-if="freeTrialForm.visitNote" class="visit-note">
            {{ freeTrialForm.visitNote }}
          </p>
          <select v-model="freeTrialForm.trialUserId" class="trial-select">
            <option value="">무료체험 슬롯 선택</option>
            <option v-for="t in availableTrialUsers" :key="t.id" :value="t.id">
              {{ t.name }}
            </option>
          </select>
          <label class="sms-check">
            <input type="checkbox" v-model="freeTrialForm.sendSms" />
            문자 발송
          </label>
        </div>
        <div class="modal-buttons">
          <button
            class="modal-btn modal-btn-cancel"
            @click="closeFreeTrialModal"
          >
            취소
          </button>
          <button
            class="modal-btn modal-btn-confirm"
            @click="confirmFreeTrial"
            :disabled="
              !freeTrialForm.name ||
              !freeTrialForm.phone ||
              !freeTrialForm.trialUserId
            "
          >
            {{ freeTrialForm.sendSms ? "확정 + 문자 발송" : "확정" }}
          </button>
        </div>
      </div>
    </div>

    <!-- 인원추가 모달 -->
    <div
      v-if="showAddMemberModal"
      class="modal-overlay"
      @click="closeAddMemberModal"
    >
      <div
        class="modal-container"
        :class="{ 'modal-container--form': addMemberCreditFree }"
        @click.stop
      >
        <h3 class="modal-title">인원 추가</h3>
        <p class="modal-message">
          {{ hhmm(selectedClass?.time) }}
          {{ classNameOf(selectedClass) }} 수업에 회원을 추가합니다.
        </p>
        <!-- 2026-10-24 사장님 결정 A: Hyrox·Running·기타는 횟수를 빼지 않는다(쓸 수 있는 회원권은 필요, 서버가 본다).
             저장된 수업 타입으로 본다(isCreditFreeClassType) -->
        <p v-if="addMemberCreditFree" class="modal-note">
          Hyrox·Running·기타 수업은 횟수권을 차감하지 않아요.
        </p>
        <div class="modal-form">
          <UiInput
            v-model="newMemberPhone"
            placeholder="이름 또는 전화번호를 입력하세요"
            type="text"
            class="phone-input"
          />
        </div>
        <div class="modal-buttons">
          <button
            class="modal-btn modal-btn-cancel"
            @click="closeAddMemberModal"
          >
            취소
          </button>
          <button
            class="modal-btn modal-btn-confirm"
            @click="confirmAddMember"
            :disabled="!newMemberPhone"
          >
            확인
          </button>
        </div>
      </div>
    </div>

    <!-- R9 수업 추가·수정 모달. 타입은 새 9개 중에서만 고른다(0절 11). 요일로 타입을 미리 고르지 않는다(1.1) -->
    <div v-if="classModal.open" class="modal-overlay" @click="closeClassModal">
      <div
        class="modal-container class-form-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="classModal.mode === 'edit' ? '수업 수정' : '수업 추가'"
        @click.stop
      >
        <h3 class="modal-title">
          {{ classModal.mode === "edit" ? "수업 수정" : "수업 추가" }}
        </h3>
        <div class="class-form">
          <label class="class-form__field">
            <span class="class-form__label">날짜</span>
            <input
              v-model="classForm.date"
              class="class-form__input"
              type="date"
              name="class-date"
              :disabled="classSaving"
            />
          </label>
          <label class="class-form__field">
            <span class="class-form__label">타입</span>
            <select
              v-model="classForm.type"
              class="class-form__input"
              name="class-type"
              :disabled="classSaving"
            >
              <option value="" disabled>
                {{
                  classForm.legacyType
                    ? `예전 타입(${classForm.legacyType}) · 새 타입을 골라 주세요`
                    : "타입을 골라 주세요"
                }}
              </option>
              <option
                v-for="t in classTypeOptions"
                :key="t.value"
                :value="t.value"
              >
                {{ t.label }}
              </option>
            </select>
          </label>
          <!-- 2026-10-24 사장님 결정 B: 기타 수업 이름(회원 앱에 보이는 이름). 기타일 때만 보이고 본문에도 기타일 때만 넣는다(classRequestBody) -->
          <label v-if="classForm.type === 'other'" class="class-form__field">
            <span class="class-form__label">이름</span>
            <input
              v-model="classForm.title"
              class="class-form__input"
              type="text"
              name="class-title"
              :maxlength="titleInputMax"
              placeholder="기타 (회원 앱에 보이는 이름)"
              autocomplete="off"
              :disabled="classSaving"
            />
          </label>
          <p v-if="classForm.type === 'other'" class="class-form__sub">
            비우면 '기타'로 보여요
          </p>
          <div class="class-form__times">
            <label class="class-form__field">
              <span class="class-form__label">시작</span>
              <input
                v-model="classForm.startTime"
                class="class-form__input"
                type="time"
                name="class-start"
                :disabled="classSaving"
              />
            </label>
            <label class="class-form__field">
              <span class="class-form__label">종료</span>
              <input
                v-model="classForm.endTime"
                class="class-form__input"
                type="time"
                name="class-end"
                :disabled="classSaving"
              />
            </label>
          </div>
          <label class="class-form__field">
            <span class="class-form__label">정원</span>
            <input
              v-model.number="classForm.capacity"
              class="class-form__input class-form__input--narrow"
              type="number"
              min="1"
              :max="capacityMax"
              step="1"
              name="class-capacity"
              :disabled="classSaving"
            />
          </label>
        </div>
        <p v-if="classServerError" class="class-form__error" role="alert">
          {{ classServerError }}
        </p>
        <p v-else-if="!classFormCheck.ok" class="class-form__hint">
          {{ classFormCheck.message }}
        </p>
        <p
          v-else-if="classModal.mode === 'edit' && !classFormDirty"
          class="class-form__hint"
        >
          바꾼 내용이 없어요.
        </p>
        <div class="modal-buttons">
          <button
            class="modal-btn modal-btn-cancel"
            type="button"
            :disabled="classSaving"
            @click="closeClassModal"
          >
            취소
          </button>
          <button
            class="modal-btn modal-btn-confirm"
            type="button"
            :disabled="!classFormCanSubmit"
            @click="submitClassForm"
          >
            {{
              classSaving
                ? "저장 중…"
                : classModal.mode === "edit"
                ? "저장"
                : "추가"
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- CAR-130 날짜 닫기 확인 창(기본값 D2): 서버가 한 번에 그 날짜 수업을 모두 지운다. 하나라도 예약(취소한 예약 포함)이나 운동 기록이
         있으면 아무것도 지우지 않는다(409, R9 "수업 취소는 만들지 않는다": 예약 취소·횟수 반환 없음) -->
    <div
      v-if="closeDate.open"
      class="modal-overlay"
      @click="closeCloseDateModal"
    >
      <div
        class="modal-container class-form-modal"
        role="dialog"
        aria-modal="true"
        aria-label="이 날짜 수업 닫기"
        @click.stop
      >
        <h3 class="modal-title">이 날짜 수업 닫기</h3>
        <p class="close-date__lead">{{ closeDatePrompt }}</p>
        <ul class="close-date__list" aria-label="삭제할 수업">
          <li
            v-for="line in closeDateLines"
            :key="line.id"
            class="close-date__item"
          >
            <span>{{ line.text }}</span>
            <span v-if="line.booked > 0" class="close-date__booked"
              >예약 {{ line.booked }}명</span
            >
          </li>
        </ul>
        <p class="class-form__hint">{{ closeDateRule }}</p>
        <p v-if="closeDate.error" class="class-form__error" role="alert">
          {{ closeDate.error }}
        </p>
        <p v-else-if="closeDateBookedNote" class="class-form__error">
          {{ closeDateBookedNote }}
        </p>
        <div class="modal-buttons">
          <button
            class="modal-btn modal-btn-cancel"
            type="button"
            :disabled="closeDate.busy"
            @click="closeCloseDateModal"
          >
            취소
          </button>
          <button
            class="modal-btn modal-btn-confirm"
            type="button"
            name="close-date-confirm"
            :disabled="!closeDateCanSubmit"
            @click="submitCloseDate"
          >
            {{ closeDate.busy ? "삭제 중…" : "모두 삭제" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ScheduleRow from "@/components/ScheduleRow.vue";
import UiInput from "@/components/UiInput.vue";
import { apiJson } from "@/lib/api";
import {
  CLASS_CAPACITY_MAX,
  CLASS_TYPES,
  addMinutesHhmm,
  weekDays,
  weekOffsetOf,
  classFormFromClass,
  classLock,
  classDisplayName,
  closeDateClassLines,
  classRequestBody,
  emptyClassForm,
  hasClassFormChanges,
  hhmm,
  isCreatableClassType,
  isCreditFreeClassType,
  effectiveClassType,
  freshClassOf,
  OTHER_CLASS_TITLE_INPUT_MAX,
  validateClassForm,
  withClassDate,
} from "@/lib/classTypes";
import {
  classHasBookingRows,
  classHasWorkouts,
  closeClassDate,
  createClass,
  deleteClass,
  describeClassError,
  describeCloseDateError,
  updateClass,
} from "@/lib/classAdminApi";
import {
  VISIT_KINDS,
  applyVisitHistory,
  visitKindLabel,
} from "@/lib/guestVisit";

const DOW = ["일", "월", "화", "수", "목", "금", "토"];

// 인원 추가·무료체험 추가는 apiJson으로 보내 오류의 상태가 error.response.status에 있고, 수업 수정·삭제(classAdminApi)는 error.status에
// 있다. 404(수업 없음 BOOKING_002)면 다른 관리자가 그 사이 그 수업을 지운 것이다(출시 최종 확인 6차)
function classGone(e) {
  const status = e && (e.status || (e.response && e.response.status));
  return status === 404;
}
// 인원 추가·무료체험 추가가 지운 수업에 닿았을 때 스케줄 위 안내 줄(수정·삭제 404와 같은 글자)
const GONE_ON_ADD = describeClassError({ status: 404 }, "update");

export default {
  name: "ClassBookingView",
  components: { ScheduleRow, UiInput },
  data() {
    return {
      // 날짜/선택(월~일 7일. R9부터 요일 제한이 없어 일요일 수업도 보이게 한다)
      days: [], // [{ date:'2025-10-01', dow:'수', num:'1' }, ...]
      selectedIdx: 0,
      // 데이터
      loading: false,
      schedule: [], // [{ id, time, end_time, type, type_label?, booked, capacity, status?, mine?, date }] (date = 요청한 날짜, withClassDate)
      scheduleSeq: 0, // 마지막 스케줄 요청 번호. 늦게 온 앞 요청의 응답은 버린다
      panelSeq: 0, // 패널에서 인원을 바꾼 뒤 고른 수업 날짜 스케줄을 다시 읽은 마지막 요청 번호(refreshAfterPanelChange)
      // 수업 선택 및 예약자 목록
      selectedClass: null,
      loadingBookings: false,
      classBookings: [], // [{ id, member_name, member_phone, status }]
      bookingsRequestedFor: "", // 마지막으로 예약자 목록을 요청한 수업 ID. 늦게 온 다른 수업의 응답은 버린다
      bookingsLoadedFor: "", // 예약자 목록을 받은 수업 ID(수정·삭제 잠금 판단)
      bookingsLoadFailed: false,
      // 고른 수업의 예약 행 유무(취소 포함, 서버 409 CLASS_HAS_BOOKINGS 기준). 예약자 목록은 확정 예약만 준다(R9 리뷰 1차)
      bookingRows: { id: "", value: null }, // value: true | false | null(모름)
      // 인원추가 모달
      showAddMemberModal: false,
      newMemberPhone: "",
      // 무료체험 추가
      showFreeTrialModal: false,
      showCancelReasonModal: false,
      cancelTarget: null,
      freeTrialForm: {
        name: "",
        phone: "",
        trialUserId: "",
        requestId: "",
        sendSms: true,
        visitKind: "trial",
        visitKindTouched: false,
        visitNote: "",
      },
      visitKinds: VISIT_KINDS,
      visitLookupTimer: null,
      trialInquiryPrefill: null,
      trialInquiryLoading: false,
      trialUsers: [],
      // 주차 네비게이션
      currentWeekOffset: 0, // 0: 이번 주, -1: 지난 주, 1: 다음 주
      // R9 수업 추가·수정 모달(lib/classTypes.js 폼, lib/classAdminApi.js API)
      classModal: { open: false, mode: "add" }, // mode: add | edit
      classForm: emptyClassForm(""),
      classEditTarget: null, // 수정 창을 연 수업(스케줄 항목 사본). 바뀐 것이 없으면 저장 버튼을 끈다
      classSaving: false,
      classServerError: "",
      scheduleNotice: "", // 추가·수정·삭제 결과(스케줄 위)
      scheduleNoticeError: false, // 안내 줄이 거절 이유인지(지운 수업의 404, 빨간 글자)
      // R9 수업 관리 상자: 고른 수업의 운동 기록 유무(삭제 잠금), 삭제 중, 결과 문구
      workoutsExist: { id: "", value: null }, // value: true | false | null(모름)
      deleting: false,
      manageMessage: "",
      manageError: false,
      // CAR-130 날짜 닫기 확인 창(기본값 D2). classes = 창을 연 순간 그 날짜 스케줄 사본, blocked = 서버가 409로 거절함(다시 보내도 같다)
      closeDate: {
        open: false,
        date: "",
        dow: "",
        classes: [],
        busy: false,
        error: "",
        blocked: false,
      },
    };
  },
  computed: {
    classTypeOptions() {
      return CLASS_TYPES;
    },
    capacityMax() {
      return CLASS_CAPACITY_MAX;
    },
    // 이름 칸 maxlength(UTF-16 단위, 40). 20자(코드 포인트) 규칙은 저장 전 검사(validateOtherClassTitle)가 본다
    titleInputMax() {
      return OTHER_CLASS_TITLE_INPUT_MAX;
    },
    // 인원 추가 창의 수업이 횟수권을 차감하지 않는 타입인가(저장된 타입 그대로, 2026-10-24 결정 A)
    addMemberCreditFree() {
      return (
        !!this.selectedClass && isCreditFreeClassType(this.selectedClass.type)
      );
    },
    classFormCheck() {
      return validateClassForm(this.classForm);
    },
    // 수정 창은 바뀐 것이 있어야 저장한다. 추가 창은 늘 true
    classFormDirty() {
      if (this.classModal.mode !== "edit") return true;
      return hasClassFormChanges(this.classEditTarget, this.classForm);
    },
    classFormCanSubmit() {
      return !this.classSaving && this.classFormCheck.ok && this.classFormDirty;
    },
    // 고른 수업의 예약자 목록을 받았는가(다른 수업의 목록이 남아 있거나 다시 받는 중이면 false)
    lockKnown() {
      const cls = this.selectedClass;
      return (
        !!cls && this.bookingsLoadedFor === cls.id && !this.loadingBookings
      );
    },
    // 수정·삭제 가능 여부. 예약(취소한 예약 포함)이나 운동 기록이 있으면 둘 다 막는다
    lock() {
      const cls = this.selectedClass;
      const workouts =
        cls && this.workoutsExist.id === cls.id
          ? this.workoutsExist.value
          : null;
      const rows =
        cls && this.bookingRows.id === cls.id ? this.bookingRows.value : null;
      return classLock(cls, this.classBookings, this.lockKnown, workouts, rows);
    },
    classBusy() {
      return this.deleting || this.classSaving || this.closeDate.busy;
    },
    // [이 날짜 수업 닫기]: 보이는 날짜의 스케줄을 다 받았고 수업이 있을 때만(지울 것이 없으면 끈다)
    canCloseDate() {
      return (
        !this.loading &&
        !this.classBusy &&
        !!this.days[this.selectedIdx] &&
        this.schedule.length > 0
      );
    },
    closeDateLines() {
      return closeDateClassLines(this.closeDate.classes);
    },
    // 확인 창 첫 줄: "2026-10-25(일) 수업 3개를 모두 삭제할까요? 되돌릴 수 없어요."
    closeDatePrompt() {
      const { date, dow } = this.closeDate;
      const day = dow ? `${date}(${dow})` : date;
      return `${day} 수업 ${this.closeDateLines.length}개를 모두 삭제할까요? 되돌릴 수 없어요.`;
    },
    closeDateRule() {
      return "휴무일처럼 수업을 열지 않는 날에 써요. 예약(취소한 예약 포함)이나 운동 기록이 있는 수업이 하나라도 있으면 아무 수업도 지우지 않아요.";
    },
    // 확정 예약이 있는 수업(스케줄 인원). 서버가 지우지 않으므로(409) 미리 알리고 [모두 삭제]를 끈다(수업 관리 상자 classLock과 같은 방식)
    closeDateBooked() {
      return this.closeDateLines.filter((l) => l.booked > 0);
    },
    closeDateBookedNote() {
      if (this.closeDateBooked.length === 0) return "";
      const names = this.closeDateBooked.map((l) => l.text).join(", ");
      return `예약이 있는 수업이 있어 이 날짜는 닫을 수 없어요: ${names}. 예약을 취소해도 취소 기록이 남아 삭제할 수 없어요.`;
    },
    closeDateCanSubmit() {
      const c = this.closeDate;
      return (
        c.open &&
        !c.busy &&
        !c.blocked &&
        this.closeDateLines.length > 0 &&
        this.closeDateBooked.length === 0
      );
    },
    availableTrialUsers() {
      const usedIds = new Set(
        this.classBookings.map((b) => b.user_id).filter(Boolean)
      );
      return this.trialUsers.filter((t) => !usedIds.has(t.id));
    },
  },
  watch: {
    // 다른 수업을 고르면(줄 클릭·인원추가·무료체험) 수업 관리 상자의 문구를 지운다. 인원추가·무료체험 버튼은 예약자 목록과
    // 운동 기록 유무를 따로 부르지 않으므로 아직 그 수업 것을 요청하지 않았으면 여기서 부른다
    selectedClass(cls, prev) {
      if (!cls) return;
      if (prev && prev.id === cls.id) return;
      this.manageMessage = "";
      this.manageError = false;
      if (this.workoutsExist.id !== cls.id) this.loadWorkoutsExist(cls.id);
      if (this.bookingsRequestedFor !== cls.id) this.loadClassBookings();
    },
    // 서버가 거절한 뒤 값을 고치면 거절 문구를 지운다(그 자리에 입력 안내가 보이게)
    classForm: {
      deep: true,
      handler() {
        if (!this.classSaving) this.classServerError = "";
      },
    },
    // 추가 창에서 시작 시각을 넣었는데 종료 시각이 비어 있으면 1시간 뒤로 채운다(고칠 수 있음)
    "classForm.startTime"(value) {
      if (!this.classModal.open || this.classForm.endTime) return;
      const end = addMinutesHhmm(value, 60);
      if (end) this.classForm.endTime = end;
    },
  },
  created() {
    this.initDays();
    this.loadSchedule();
    this.loadTrialUsers();
  },
  methods: {
    hhmm,
    // 화면에 보일 수업 이름: 기타 수업은 관리자가 정한 이름(비면 "기타"), 그 밖에는 타입 표시명(lib/classTypes.js classDisplayName)
    classNameOf(cls) {
      return classDisplayName(cls);
    },
    // 운동 프로그램 탭 주소: 그 수업의 날짜·타입(예전 타입이면 날짜만)
    programLink(cls) {
      const query = {};
      if (cls && cls.date) query.date = cls.date;
      // 예전 타입 + 새 타입 이름 제목(D22) 수업은 그 새 타입 프로그램(서버와 같은 규칙)
      const type = cls ? effectiveClassType(cls.type, cls.title) : "";
      if (isCreatableClassType(type)) query.type = type;
      return { path: "/programs", query };
    },
    trialRecordLink(booking) {
      return {
        path: "/free-trial-requests",
        query: { booking_id: booking.id },
      };
    },
    // ---------- R9 수업 추가·수정·삭제 ----------
    openAddClassModal() {
      this.classForm = emptyClassForm(this.days[this.selectedIdx]?.date);
      this.classEditTarget = null;
      this.classServerError = "";
      this.classModal = { open: true, mode: "add" };
    },
    openEditClassModal() {
      const cls = this.selectedClass;
      if (!cls || !this.lock.editable || this.classBusy) return;
      this.classEditTarget = { ...cls };
      this.classForm = classFormFromClass(cls);
      this.classServerError = "";
      this.classModal = { open: true, mode: "edit" };
    },
    closeClassModal() {
      if (this.classSaving) return;
      this.classModal = { ...this.classModal, open: false };
    },
    // 저장. 실패하면 창을 열어 둔 채 이유를 보인다(서버 409·422 문구, 전역 오류 팝업 없음)
    async submitClassForm() {
      if (!this.classFormCanSubmit) return;
      const mode = this.classModal.mode;
      const target = this.classEditTarget;
      const body = classRequestBody(this.classForm);
      this.classSaving = true;
      this.classServerError = "";
      try {
        let keepId = "";
        if (mode === "edit") {
          await updateClass(target.id, body);
          keepId = target.id;
        } else {
          const created = await createClass(body);
          keepId = created && created.id ? String(created.id) : "";
        }
        this.classModal = { ...this.classModal, open: false };
        const what = `${body.date} ${body.start_time} ${classDisplayName({
          type: body.type,
          title: body.title,
        })}`;
        await this.showClassDate(body.date, keepId);
        this.scheduleNoticeError = false;
        this.scheduleNotice =
          mode === "edit"
            ? `수업을 고쳤어요: ${what}`
            : `수업을 추가했어요: ${what}`;
      } catch (e) {
        console.error("수업 저장 실패:", e);
        // 고치려던 수업이 그 사이 지워졌으면(404) 창을 닫고 선택을 풀어 지운 줄을 스케줄에서 뺀다(forgetGoneClass). 창에 두면
        // 없는 수업을 다시 저장해 404가 되풀이됐다(출시 최종 확인 1차)
        if (mode === "edit" && e && e.status === 404) {
          this.classModal = { ...this.classModal, open: false };
          this.forgetGoneClass(target.id, describeClassError(e, "update"));
          return;
        }
        this.classServerError = describeClassError(
          e,
          mode === "edit" ? "update" : "create"
        );
        // 그 사이 예약·기록이 생겼으면(409) 예약자·스케줄·기록 유무를 새로 받아 잠금을 보인다
        if (mode === "edit" && e && e.status === 409) {
          this.loadSchedule();
          if (this.selectedClass && this.selectedClass.id === target.id) {
            this.loadClassBookings();
            if (e.code === "CLASS_HAS_WORKOUTS") {
              this.workoutsExist = { id: target.id, value: true };
            }
            if (e.code === "CLASS_HAS_BOOKINGS") {
              this.bookingRows = { id: target.id, value: true };
            }
          }
        }
      } finally {
        this.classSaving = false;
      }
    },
    async confirmDeleteClass() {
      const cls = this.selectedClass;
      if (!cls || !this.lock.deletable || this.classBusy) return;
      const what = `${cls.date} ${hhmm(cls.time)} ${classDisplayName(cls)}`;
      if (!confirm(`${what} 수업을 삭제할까요? 되돌릴 수 없어요.`)) return;
      this.deleting = true;
      this.manageMessage = "";
      this.manageError = false;
      try {
        await deleteClass(cls.id);
        if (this.selectedClass && this.selectedClass.id === cls.id) {
          this.selectedClass = null;
          this.classBookings = [];
          this.bookingsLoadedFor = "";
        }
        this.scheduleNoticeError = false;
        this.scheduleNotice = `수업을 삭제했어요: ${what}`;
        this.loadSchedule();
      } catch (e) {
        console.error("수업 삭제 실패:", e);
        // 그 사이 다른 관리자가 지웠으면(404) 선택을 풀고 스케줄을 다시 받는다(지운 수업이 상자에 남아 [수업 수정]·[수업 삭제]가
        // 켜진 채 404가 되풀이되던 것, 출시 최종 확인 1차)
        if (e && e.status === 404) {
          this.forgetGoneClass(cls.id, describeClassError(e, "delete"));
          return;
        }
        const showsThisClass =
          !!this.selectedClass && this.selectedClass.id === cls.id;
        if (showsThisClass) {
          this.manageError = true;
          this.manageMessage = describeClassError(e, "delete");
        }
        if (e && e.code === "CLASS_HAS_WORKOUTS" && showsThisClass) {
          this.workoutsExist = { id: cls.id, value: true };
        } else if (e && e.status === 409) {
          if (e.code === "CLASS_HAS_BOOKINGS" && showsThisClass) {
            this.bookingRows = { id: cls.id, value: true };
          }
          this.loadSchedule();
          if (showsThisClass) this.loadClassBookings();
        }
      } finally {
        this.deleting = false;
      }
    },
    // 서버가 수정·삭제에 404(수업 없음)로 답하면 다른 관리자가 그 수업을 지운 것이다. 그 수업이 아직 골라져 있으면 선택·예약자 목록을
    // 풀고(늦게 올 그 수업의 예약자 응답도 버림) 스케줄을 다시 받아 지운 줄을 뺀다. 이유는 스케줄 위 안내 줄(scheduleNotice)에 보인다:
    // 수업 관리 상자는 선택이 풀리면 사라진다. loadSchedule은 새 목록에 없는 선택을 풀지 않는다(다른 요일 탭을 보는 동안에도 패널의
    // 수업을 유지하는 규칙). 그래서 404 때만 여기서 푼다. 예전에는 지운 수업이 상자에 남아 수정·삭제 버튼이 켜져 있었다(출시 최종 확인 1차)
    forgetGoneClass(id, message) {
      if (this.selectedClass && this.selectedClass.id === id) {
        this.clearSelection();
      }
      this.scheduleNotice = message;
      this.scheduleNoticeError = true;
      this.loadSchedule();
    },
    // 고른 수업·예약자 목록을 푼다(늦게 올 그 수업의 예약자 응답도 버림). 지운 수업(404)·닫은 날짜의 수업에 쓴다
    clearSelection() {
      this.selectedClass = null;
      this.classBookings = [];
      this.bookingsLoadedFor = "";
      this.bookingsRequestedFor = "";
      this.loadingBookings = false;
      this.manageMessage = "";
      this.manageError = false;
    },
    // ---------- CAR-130 날짜 닫기(2026-10-24 기본값 D2) ----------
    // 확인 창을 연다. 보이는 날짜의 스케줄(다 받은 것)을 사본으로 남겨 창에 보인다
    openCloseDateModal() {
      if (!this.canCloseDate) return;
      const day = this.days[this.selectedIdx];
      this.closeDate = {
        open: true,
        date: day.date,
        dow: day.dow || "",
        classes: this.schedule.map((c) => ({ ...c })),
        busy: false,
        error: "",
        blocked: false,
      };
    },
    closeCloseDateModal() {
      if (this.closeDate.busy) return;
      this.closeDate = { ...this.closeDate, open: false };
    },
    // DELETE /v1/admin/classes?date=. 지우면 창을 닫고 스케줄 위에 지운 수를 보인다(그 날짜 수업을 고르고 있었으면 선택을 푼다).
    // 실패하면 창을 열어 둔 채 이유를 보인다: 409(예약·기록 있는 수업, 아무것도 안 지움)는 막힌 수업을 적고 [모두 삭제]를 끈다.
    // 어느 쪽이든 그 날짜 스케줄을 다시 받는다(지운 줄을 빼거나, 거절이면 새 인원을 창 목록에도 넣음)
    async submitCloseDate() {
      if (!this.closeDateCanSubmit) return;
      const date = this.closeDate.date;
      this.closeDate = { ...this.closeDate, busy: true, error: "" };
      try {
        const n = await closeClassDate(date);
        this.closeDate = { ...this.closeDate, open: false, busy: false };
        if (this.selectedClass && this.selectedClass.date === date) {
          this.clearSelection();
        }
        this.scheduleNoticeError = false;
        this.scheduleNotice =
          n === 0
            ? `${date}에는 지울 수업이 없었어요.`
            : n === null
            ? `${date} 수업을 모두 삭제했어요.`
            : `${date} 수업 ${n}개를 삭제했어요.`;
        await this.loadSchedule();
      } catch (e) {
        console.error("날짜 닫기 실패:", e);
        const blocked = !!e && e.status === 409;
        this.closeDate = {
          ...this.closeDate,
          busy: false,
          error: describeCloseDateError(e),
          blocked,
        };
        const applied = await this.loadSchedule();
        const shown = this.days[this.selectedIdx]?.date;
        // 409면 막은 수업이 남아 있다: 빈 목록은 스케줄을 못 받은 것이라 창의 사본을 그대로 둔다
        if (
          blocked &&
          applied &&
          this.closeDate.open &&
          shown === date &&
          this.schedule.length > 0
        ) {
          this.closeDate = {
            ...this.closeDate,
            classes: this.schedule.map((c) => ({ ...c })),
          };
        }
        // 고른 수업이 그 날짜 것이면 예약 행·운동 기록 유무를 새로 받아 수업 관리 상자의 잠금도 맞춘다
        const cls = this.selectedClass;
        if (blocked && cls && cls.date === date) {
          this.loadClassBookings();
          this.loadWorkoutsExist(cls.id);
        }
      }
    },
    // 추가·수정한 수업의 날짜로 옮겨 스케줄을 다시 받는다. keepId(수정한 수업, 추가 응답의 새 수업 ID)가 있으면
    // 새 목록의 그 수업을 고르고, 그 날짜에 없으면(고른 수업이 그 수업일 때) 선택을 푼다
    async showClassDate(date, keepId) {
      this.goToDate(date);
      const applied = await this.loadSchedule();
      if (!applied || !keepId) return;
      const found = this.schedule.find((c) => c.id === keepId);
      if (found) {
        this.selectedClass = found;
        this.loadClassBookings();
      } else if (this.selectedClass && this.selectedClass.id === keepId) {
        this.selectedClass = null;
        this.classBookings = [];
        this.bookingsLoadedFor = "";
      }
    },
    async loadWorkoutsExist(id) {
      this.workoutsExist = { id, value: null };
      const value = await classHasWorkouts(id);
      if (this.workoutsExist.id === id) this.workoutsExist = { id, value };
    },
    // ---------- 예약자 칸 배지 ----------
    // 기준 날짜는 "오늘"이 아니라 보고 있는 수업의 날짜다. 지난 수업을 열면 그때 시점으로 읽힌다.
    classDate() {
      return this.days[this.selectedIdx]?.date || "";
    },
    // 만료까지 남은 일수. 만료일이나 기준 날짜가 없으면 null
    daysToExpiry(booking) {
      const base = this.classDate();
      if (!booking.membership_expired_at || !base) return null;
      const diff =
        (new Date(`${booking.membership_expired_at}T00:00:00`) -
          new Date(`${base}T00:00:00`)) /
        86400000;
      return Number.isFinite(diff) ? Math.round(diff) : null;
    },
    membershipText(booking) {
      if (!booking.membership_type) return "회원권 없음";
      // remaining_count가 음수면 기간제(Unlimited 등)라 횟수가 뜻이 없다
      const left =
        booking.membership_remaining >= 0
          ? `${booking.membership_remaining}회 남음`
          : "";
      const d = this.daysToExpiry(booking);
      const until =
        d === null
          ? ""
          : d < 0
          ? "만료됨"
          : d === 0
          ? "오늘 만료"
          : `${d}일 남음`;
      return [booking.membership_type, left, until].filter(Boolean).join(" · ");
    },
    membershipBadgeClass(booking) {
      if (!booking.membership_type) return "badge-danger";
      const d = this.daysToExpiry(booking);
      if (d !== null && d < 0) return "badge-danger";
      // 남은 횟수 2회 이하나 만료 14일 이내면 재등록 대화를 꺼낼 때다
      if (
        (d !== null && d <= 14) ||
        (booking.membership_remaining >= 0 && booking.membership_remaining <= 2)
      ) {
        return "badge-warn";
      }
      return "badge-plain";
    },
    visitText(booking) {
      if (booking.attended_count === 0) return "첫 수업";
      const nth = `${booking.attended_count + 1}회차`;
      // -1은 직전 출석이 없다는 뜻이라 공백을 말할 수 없다
      if (booking.days_since_last < 0) return nth;
      if (booking.days_since_last === 0) return `${nth} · 같은 날 또 옴`;
      return `${nth} · ${booking.days_since_last}일 만에`;
    },
    visitBadgeClass(booking) {
      if (booking.attended_count === 0) return "badge-new";
      // 14일 이상 비었으면 환영 멘트가 필요한 회원
      return booking.days_since_last >= 14 ? "badge-warn" : "badge-plain";
    },

    // ---------- 날짜 초기화/선택 ----------
    // 날짜 탭 = 오늘이 든 주(월~일)에서 offset주 뒤의 7일(lib/classTypes.js weekDays). R9: 요일 제한이 없어 일요일 수업도 보이게
    // 7일이고, 일요일에 열어도 오늘(일요일)이 든 주가 "이번 주"다(R9 리뷰 1차, 운동 프로그램 탭·실시간 심박과 같은 주)
    weekTabs(offset) {
      return weekDays(new Date(), offset).map((dt) => ({
        date: this.toYmd(dt),
        dow: DOW[dt.getDay()],
        num: String(dt.getDate()),
      }));
    },
    initDays() {
      this.currentWeekOffset = 0;
      this.days = this.weekTabs(0);
      // 오늘 탭을 고른다(이번 주에는 늘 오늘이 있다)
      const todayIdx = this.days.findIndex(
        (d) => d.date === this.toYmd(new Date())
      );
      this.selectedIdx = todayIdx >= 0 ? todayIdx : 0;
    },
    selectDay(i) {
      if (i === this.selectedIdx) return;
      this.selectedIdx = i;
      this.scheduleNotice = "";
      this.scheduleNoticeError = false;
      this.loadSchedule();
    },
    toYmd(d) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },
    // 그 날짜가 보이는 주로 옮기고 그 날짜 탭을 고른다(스케줄은 부르지 않는다). 날짜가 아니면 그대로.
    // 주 번호는 updateWeekDays와 같은 기준(오늘이 든 주 = 0주, 일요일도 그 주)으로 센다(lib/classTypes.js weekOffsetOf)
    goToDate(ymd) {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
      if (!m) return;
      const target = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const weeks = weekOffsetOf(new Date(), target);
      if (weeks === null) return;
      if (weeks !== this.currentWeekOffset) {
        this.currentWeekOffset = weeks;
        this.updateWeekDays();
      }
      const idx = this.days.findIndex((d) => d.date === ymd);
      if (idx >= 0) this.selectedIdx = idx;
    },

    // ---------- 백엔드 호출 ----------
    // 응답을 목록에 넣었으면 true, 늦게 와서 버렸으면 false
    async loadSchedule() {
      // 날짜·주를 빨리 옮기면 앞 요청의 응답이 나중에 올 수 있다. 마지막 요청의 응답만 쓴다(목록이 고른 날짜 탭과 늘 같게)
      const seq = ++this.scheduleSeq;
      this.loading = true;
      const date = this.days[this.selectedIdx]?.date;
      try {
        // 관리자용 스케줄 API 호출
        const data = await apiJson(
          `/v1/admin/classes/schedule?date=${encodeURIComponent(date)}`
        );
        if (seq !== this.scheduleSeq) return false;
        // 기대 스키마: { classes: [{ id, time:"06:00:00", end_time:"07:00", type:"pull_cardio", type_label?, booked:4, capacity:12, status?, mine? }] }
        // 항목에는 날짜가 없어 요청한 날짜를 붙인다(수업 관리 상자·수정 창은 그 수업 날짜를 쓴다)
        this.schedule = withClassDate(
          Array.isArray(data.classes) ? data.classes : [],
          date
        );
        // 고른 수업이 새 목록에 있으면 새 줄로 바꾼다: 패널에서 인원을 추가·취소한 뒤 머리의 인원("1 / 12명")이 스케줄 줄("0 / 12")과
        // 다르게 남지 않게(R9 리뷰 2차). 같은 수업 ID라 예약자 목록은 다시 부르지 않는다(selectedClass watch)
        const fresh =
          this.selectedClass &&
          this.schedule.find((c) => c.id === this.selectedClass.id);
        if (fresh) this.selectedClass = fresh;
        return true;
      } catch {
        if (seq !== this.scheduleSeq) return false;
        this.schedule = [];
        return true;
      } finally {
        if (seq === this.scheduleSeq) this.loading = false;
      }
    },
    // 패널에서 인원을 바꾼 뒤(예약 취소·인원 추가·무료체험) 스케줄과 고른 수업의 머리 인원을 새로 받는다. loadSchedule은 보이는
    // 날짜 탭의 수업만 읽으므로, 다른 요일 탭을 보는 동안 패널에서 바꿨으면 고른 수업 날짜의 스케줄을 따로 읽어 그 수업 줄로 바꾼다
    // (R10 합치기, STATUS "R9 리뷰에서 열린 것": 머리 인원이 그대로 남던 것). 보이는 날짜 목록은 그대로 두고, 늦게 온 앞 응답이나
    // 그 사이 다른 수업을 골랐으면 쓰지 않는다
    async refreshAfterPanelChange() {
      const cls = this.selectedClass;
      const shown = this.days[this.selectedIdx]?.date;
      const seq = ++this.panelSeq;
      const tasks = [this.loadSchedule()];
      if (cls && cls.date && cls.date !== shown) {
        tasks.push(
          apiJson(
            `/v1/admin/classes/schedule?date=${encodeURIComponent(cls.date)}`
          )
            .then((data) => {
              if (seq !== this.panelSeq) return;
              const fresh = freshClassOf(cls, data && data.classes, cls.date);
              if (
                fresh &&
                this.selectedClass &&
                this.selectedClass.id === cls.id
              )
                this.selectedClass = fresh;
            })
            // 읽지 못하면 머리 인원은 그대로 둔다(apiJson이 전역 오류 팝업을 띄움)
            .catch(() => {})
        );
      }
      await Promise.all(tasks);
    },
    // ---------- 주차 네비게이션 메서드 ----------
    goToPreviousWeek() {
      this.scheduleNotice = "";
      this.scheduleNoticeError = false;
      this.currentWeekOffset -= 1;
      this.updateWeekDays();
      this.loadSchedule();
    },
    goToNextWeek() {
      this.scheduleNotice = "";
      this.scheduleNoticeError = false;
      this.currentWeekOffset += 1;
      this.updateWeekDays();
      this.loadSchedule();
    },
    updateWeekDays() {
      this.days = this.weekTabs(this.currentWeekOffset);
      // 첫 번째 날짜(월요일) 선택
      this.selectedIdx = 0;
    },
    getCurrentWeekText() {
      if (this.currentWeekOffset === 0) {
        return "이번 주";
      } else if (this.currentWeekOffset === -1) {
        return "지난 주";
      } else if (this.currentWeekOffset === 1) {
        return "다음 주";
      } else if (this.currentWeekOffset < 0) {
        return `${Math.abs(this.currentWeekOffset)}주 전`;
      } else {
        return `${this.currentWeekOffset}주 후`;
      }
    },
    // ---------- 수업 선택 및 예약자 관리 ----------
    // 줄을 누를 때마다(같은 수업을 다시 눌러도) 예약자 목록과 운동 기록 유무를 새로 받는다
    selectClass(cls) {
      this.selectedClass = cls;
      this.loadClassBookings();
      this.loadWorkoutsExist(cls.id);
    },
    async loadClassBookings() {
      if (!this.selectedClass) return;
      // 응답을 기다리는 사이 다른 수업을 고르면 앞 수업의 목록은 버린다(수정·삭제 잠금이 그 수업 목록으로만 정해지게)
      const id = this.selectedClass.id;
      this.bookingsRequestedFor = id;
      this.loadingBookings = true;
      this.bookingsLoadFailed = false;
      // 예약 행 유무(취소 포함)도 같이 받는다. 목록은 확정 예약만이라, 예약이 모두 취소된 수업은 이 값으로만 잠근다(R9 리뷰 1차).
      // 잠금은 목록과 이 값을 모두 받은 뒤 정한다(loadingBookings). 이 값을 못 받으면 모름(null): 켜 두고 서버 409를 보인다
      const rowsPromise = classHasBookingRows(id);
      try {
        // 백엔드 API 호출
        const data = await apiJson(`/v1/admin/classes/${id}/bookings`);
        const rows = await rowsPromise;
        if (this.bookingsRequestedFor !== id) return;
        this.classBookings = data.bookings || [];
        this.bookingRows = { id, value: rows };
        this.bookingsLoadedFor = id;
      } catch (error) {
        if (this.bookingsRequestedFor !== id) return;
        console.error("예약자 목록 로드 실패:", error);
        this.classBookings = [];
        this.bookingsLoadedFor = "";
        this.bookingsLoadFailed = true;
      } finally {
        if (this.bookingsRequestedFor === id) this.loadingBookings = false;
      }
    },
    async loadTrialUsers() {
      try {
        const res = await apiJson(
          "/v1/admin/members/search?q=%EB%AC%B4%EB%A3%8C%EC%B2%B4%ED%97%98"
        );
        this.trialUsers = res.members || [];
      } catch {
        this.trialUsers = [];
      }
    },
    trialRequestId() {
      return String(this.$route?.query?.trial_request_id || "");
    },
    openFreeTrialModal() {
      const requestId = this.trialRequestId();
      const inquiry =
        this.trialInquiryPrefill?.request?.id === requestId
          ? this.trialInquiryPrefill.request
          : null;
      this.freeTrialForm = {
        // 이름·전화번호는 조작 가능한 query가 아니라 서버의 문의 원본만 쓴다.
        name: inquiry?.name || "",
        phone: inquiry?.phone_number || "",
        trialUserId: "",
        requestId: inquiry ? requestId : "",
        sendSms: true,
        visitKind: "trial",
        visitKindTouched: false,
        visitNote: "",
      };
      this.showFreeTrialModal = true;
    },
    visitKindLabel,
    pickVisitKind(kind) {
      this.freeTrialForm.visitKind = kind;
      this.freeTrialForm.visitKindTouched = true;
    },
    // 번호를 다 넣으면 참석 기록을 보고, 전에 참석한 번호면 드랍인을 미리 고른다(코치가 고른 종류는 그대로)
    onFreeTrialPhone(phone) {
      clearTimeout(this.visitLookupTimer);
      this.visitLookupTimer = setTimeout(
        () => applyVisitHistory(this.freeTrialForm, phone),
        300
      );
    },
    async loadTrialInquiryPrefill() {
      const requestId = this.trialRequestId();
      if (!requestId) {
        this.trialInquiryPrefill = null;
        return null;
      }
      if (this.trialInquiryPrefill?.request?.id === requestId)
        return this.trialInquiryPrefill;
      if (this.trialInquiryLoading) return null;
      this.trialInquiryLoading = true;
      try {
        const detail = await apiJson(
          `/v1/staff/trial-customers/${encodeURIComponent(requestId)}`
        );
        if (this.trialRequestId() !== requestId) return null;
        this.trialInquiryPrefill = detail;
        return detail;
      } catch (error) {
        console.error("체험 문의 불러오기 실패:", error);
        this.trialInquiryPrefill = null;
        return null;
      } finally {
        this.trialInquiryLoading = false;
      }
    },
    async openFreeTrialForClass(cls) {
      this.selectedClass = cls;
      if (this.trialRequestId()) {
        const detail = await this.loadTrialInquiryPrefill();
        // 문의 원본을 확인하지 못한 상태에서 이름·번호를 직접 입력해 새 신청을
        // 만들지 않는다. 재시도하면 원본을 다시 불러온다.
        if (!detail) return;
      }
      this.openFreeTrialModal();
    },
    closeFreeTrialModal() {
      this.showFreeTrialModal = false;
    },
    async confirmFreeTrial() {
      if (
        !this.freeTrialForm.name ||
        !this.freeTrialForm.phone ||
        !this.freeTrialForm.trialUserId ||
        !this.selectedClass
      )
        return;
      try {
        const requestId = this.freeTrialForm.requestId;
        await apiJson(`/v1/admin/classes/${this.selectedClass.id}/free-trial`, {
          method: "POST",
          body: JSON.stringify({
            name: this.freeTrialForm.name,
            phone_number: this.freeTrialForm.phone,
            trial_user_id: this.freeTrialForm.trialUserId,
            ...(requestId ? { request_id: requestId } : {}),
            send_sms: this.freeTrialForm.sendSms,
            visit_kind: this.freeTrialForm.visitKind,
          }),
        });
        this.closeFreeTrialModal();
        // 같은 문의를 실수로 다른 수업에도 다시 연결하지 않도록 성공 뒤에만
        // 체험 고객 관리에서 받은 일회성 prefill query를 지운다.
        if (requestId) this.$router.replace({ path: "/booking" });
        this.loadClassBookings();
        this.refreshAfterPanelChange();
      } catch (e) {
        console.error("무료체험 추가 실패:", e);
        // 그 사이 다른 관리자가 지운 수업이면(404) 창을 닫고 선택을 풀어 지운 줄을 뺀다(출시 최종 확인 6차, 아래 인원 추가와 같음)
        if (classGone(e)) {
          const id = this.selectedClass && this.selectedClass.id;
          this.closeFreeTrialModal();
          this.forgetGoneClass(id, GONE_ON_ADD);
        }
      }
    },
    addMember(cls) {
      this.selectedClass = cls;
      this.newMemberPhone = "";
      this.showAddMemberModal = true;
    },
    closeAddMemberModal() {
      this.showAddMemberModal = false;
      this.newMemberPhone = "";
    },
    async confirmAddMember() {
      if (!this.newMemberPhone || !this.selectedClass) return;

      try {
        // 백엔드 API 호출
        await apiJson(`/v1/admin/classes/${this.selectedClass.id}/bookings`, {
          method: "POST",
          body: JSON.stringify({
            query: this.newMemberPhone,
          }),
        });

        console.log("인원 추가:", this.selectedClass.id, this.newMemberPhone);
        this.closeAddMemberModal();
        this.loadClassBookings(); // 목록 새로고침
        this.refreshAfterPanelChange(); // 스케줄·머리 인원 새로고침
      } catch (error) {
        console.error("인원 추가 실패:", error);
        // 실패 문구는 apiJson의 전역 오류 팝업이 보인다. 그 사이 다른 관리자가 지운 수업이면(404 BOOKING_002) 인원 추가 창을 닫고
        // 선택을 풀어 스케줄을 다시 받는다(forgetGoneClass). 예전에는 서버가 500이었고 창·지운 줄·켜진 [수업 수정]·[수업 삭제]가
        // 그대로 남아 같은 실패가 되풀이됐다(출시 최종 확인 6차)
        if (classGone(error)) {
          const id = this.selectedClass && this.selectedClass.id;
          this.closeAddMemberModal();
          this.forgetGoneClass(id, GONE_ON_ADD);
        }
      }
    },
    async markAttendance(bookingId) {
      try {
        // 백엔드 API 호출
        await apiJson(`/v1/admin/bookings/${bookingId}/attendance`, {
          method: "POST",
        });

        console.log("출석 처리 완료:", bookingId);
        this.loadClassBookings(); // 목록 새로고침
      } catch (error) {
        console.error("출석 처리 실패:", error);
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
      }
    },
    cancelBooking(booking) {
      // 게스트(무료체험) 예약은 사유 선택 모달 — 고객 취소 vs 관리자 정정에 따라
      // 무료체험 신청 이력 처리가 달라진다
      if (booking.guest_name) {
        this.cancelTarget = booking;
        this.showCancelReasonModal = true;
        return;
      }
      if (!confirm("이 예약을 취소하시겠습니까?")) {
        return;
      }
      this.requestCancel(booking.id, "customer");
    },
    async doCancelBooking(reason) {
      if (!this.cancelTarget) return;
      const id = this.cancelTarget.id;
      this.showCancelReasonModal = false;
      this.cancelTarget = null;
      await this.requestCancel(id, reason);
    },
    async requestCancel(bookingId, reason) {
      try {
        // 백엔드 API 호출 (관리자용 예약 취소)
        await apiJson(
          `/v1/admin/bookings/${bookingId}?reason=${encodeURIComponent(
            reason
          )}`,
          { method: "DELETE" }
        );

        console.log("예약 취소:", bookingId, reason);
        this.loadClassBookings(); // 목록 새로고침
        this.refreshAfterPanelChange(); // 스케줄·머리 인원 새로고침
      } catch (error) {
        console.error("예약 취소 실패:", error);
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
      }
    },
    getBookingStatusText(status) {
      const statusMap = {
        confirmed: "확정",
        attended: "출석",
        absent: "결석",
        cancelled: "취소",
      };
      return statusMap[status] || status;
    },
    getBookingStatusClass(status) {
      const classMap = {
        confirmed: "chip--warn",
        attended: "chip--done",
        absent: "chip--close",
        cancelled: "chip--close",
      };
      return classMap[status] || "";
    },
    goToMemberProfile(userId) {
      this.$router.push(`/profile/${userId}`);
    },
    getAttendanceStatusText(attended) {
      if (attended === null) return "미처리";
      if (attended === true) return "출석 완료";
      return "불참 처리";
    },
    getAttendanceStatusClass(attended) {
      if (attended === null) return "chip--warn";
      if (attended === true) return "chip--done";
      return "chip--close";
    },
  },
};
</script>

<style scoped>
/* 메인: 중앙(클래스 예약) + 우측(내 예약) 2열 그리드 */
.booking-content {
  display: grid;
  /* 예약자 칸에 특이사항·회원권·출석 이력이 들어가면서 360px로는 글자가 계속 접혔다.
     수업은 세로 목록이라 좁아져도 읽히므로 폭을 예약자 쪽으로 넘긴다. */
  grid-template-columns: 1fr 480px;
  gap: 24px;
  align-items: start; /* 두 칼럼 위 정렬 */
  min-width: 0; /* 자식(그리드/텍스트) 폭 확장 시 줄바꿈 방지 */
}

/* 우측 칼럼 래퍼 (원하면 sticky로 고정 가능) */
.right {
  width: 480px;
  min-width: 480px;
  align-self: start;
  /* 스크롤 고정 원하시면 아래 주석 해제
  position: sticky;
  top: 0;
  */
}

/* 중앙 패널 */
.panel {
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  min-width: 0; /* 내부 콘텐츠가 길어져도 우측 칼럼 밀리지 않게 */
}

.panel-head {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-head h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
}

/* R9 수업 추가 버튼·결과 문구 */
.panel-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
/* CAR-130 날짜 닫기 버튼(수업 관리 상자의 [수업 삭제]와 같은 빨간 테두리, 높이는 [+ 수업 추가]와 같게) */
.close-date-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 5px;
  border: 1px solid #7d2828;
  background: #1b1d1e;
  color: #ff817f;
  font-size: 14px;
  cursor: pointer;
}
.close-date-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
/* 날짜 닫기 확인 창의 수업 목록 */
.close-date__lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: #c8c8c8;
}
.close-date__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 260px;
  overflow-y: auto;
}
.close-date__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #151718;
  font-size: 14px;
  color: #fff;
}
.close-date__booked {
  flex-shrink: 0;
  font-size: 13px;
  color: #ff9500;
}
.add-class-btn {
  height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 5px;
  background: #78ff47;
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.add-class-btn:disabled {
  background: #3a3c3d;
  color: #8e8e8e;
  cursor: not-allowed;
}
.schedule-notice {
  margin: 0;
  font-size: 13px;
  color: #78ff47;
}

/* 주간 날짜 버튼 */
.week {
  display: grid;
  /* 7일(R9 일요일 수업)이 가운데 칸에 다 들어가게. origin/main이 오른쪽 예약자 칸을 480px로 넓혀 1280 노트북에서 6칸 기준
     636px(96px × 6 + 12px × 5)로 잡은 폭 안에 7 × 72px + 6 × 8px = 552px로 들어간다(앱 v2 병합 때 확인) */
  grid-template-columns: repeat(7, minmax(72px, 1fr));
  gap: 8px;
  width: 100%;
  overflow-x: auto;
}

.day {
  width: 100%;
  height: 64px;
  background: #1b1d1e;
  border-radius: 6px;
  color: #fff;
  border: 0;
  cursor: pointer;
}

.day.sel {
  background: #d8d8d8;
  color: #1b1d1e;
}

.dow {
  display: block;
  font-size: 14px;
  opacity: 0.9;
  margin-top: 6px;
}

.num {
  display: block;
  font-size: 16px;
  font-weight: 500;
}

/* 스케줄 리스트 */
.rows {
  display: grid;
  gap: 12px;
}

.empty {
  padding: 20px 8px;
  color: #c8c8c8;
}

/* 우측: 내 예약 패널 */
.right .my-panel {
  background: #151718;
  border-radius: 10px;
  padding: 0;
  height: 800px;
  display: flex;
  flex-direction: column;
}

.my-panel-header {
  padding: 16px 12px 12px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(114, 114, 114, 0.2);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
}

.panel-title {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
}

.tabs {
  display: flex;
  gap: 8px;
  align-items: center;
}

.tab {
  height: 34px;
  padding: 0 12px;
  border-radius: 5px;
  background: #1b1d1e;
  color: #8e8e8e;
  border: 0;
  cursor: pointer;
}

.tab--active {
  background: #d8d8d8;
  color: #000;
  font-weight: 600;
}

.my-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  scrollbar-width: thin;
  scrollbar-color: #333 transparent;
}

.my-panel-content::-webkit-scrollbar {
  width: 6px;
}

.my-panel-content::-webkit-scrollbar-track {
  background: transparent;
}

.my-panel-content::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 3px;
}

.my-panel-content::-webkit-scrollbar-thumb:hover {
  background: #444;
}

.my-empty {
  padding: 12px;
  color: #c8c8c8;
}

.my-item {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.my-item:last-child {
  margin-bottom: 0;
}

.my-item-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.my-item .time {
  color: #c8c8c8;
  font-size: 14px;
}

.my-item-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.my-item .type {
  font-size: 18px;
  font-weight: 600;
}

.my-item .type.hyb {
  color: #49de80;
}
.my-item .type.core {
  color: #f59e0c;
}
.my-item .type.car {
  color: #ef4444;
}

.chip {
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border-radius: 5px;
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;
}

.chip--attended {
  border: 1px solid #333;
  color: #8e8e8e;
  background: #1b1d1e;
}

.chip--absent {
  border: 1px solid #ef4444;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.chip--cancel {
  border: 1px solid #7d2828;
  color: #ef4444;
  background: #2d1a1a;
}

.chip--clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}
.chip--clickable:hover {
  opacity: 0.8;
}
.chip--clickable:active {
  opacity: 0.6;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  box-sizing: border-box;
  width: 510px;
  height: 198px;
  background: #1b1d1e;
  border: 1px solid #3a3c3d;
  border-radius: 10px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* 입력 칸이 여러 개인 창(무료체험·드랍인 추가)은 내용만큼 늘어난다. 고정 높이 198px이면 칸이 창 밖으로 나왔다 */
.modal-container--form {
  height: auto;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
}

.modal-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #ffffff;
}

.modal-message {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 110%;
  letter-spacing: -0.02em;
  color: #8e8e8e;
  flex: 1;
}

/* 인원 추가 창의 차감 안내(.modal-message와 달리 늘어나지 않는다) */
.modal-note {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-size: 13px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #f59e0c;
}

.modal-buttons {
  display: flex;
  gap: 20px;
  justify-content: flex-end;
}

.modal-btn {
  width: 84px;
  height: 36px;
  border-radius: 5px;
  font-family: "Pretendard", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 110%;
  letter-spacing: -0.02em;
  color: #ffffff;
  border: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.modal-btn:hover {
  opacity: 0.8;
}
.modal-btn-cancel {
  box-sizing: border-box;
  background: #1b1d1e;
  border: 1px solid #333333;
}
.modal-btn-confirm {
  background: #ff4647;
}

/* 반응형: 1200px 이하에서는 1열 스택 (예약자 칸이 480px로 넓어져 1100에서는 중앙이 눌렸다) */
@media (max-width: 1200px) {
  .booking-content {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .right {
    width: auto;
    min-width: 0;
  }
}

/* 더 작은 화면: 날짜 선택을 가로 스크롤로 전환 */
@media (max-width: 720px) {
  .panel-head h2 {
    font-size: 20px;
  }

  .week {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 6px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
  }
  .day {
    flex: 0 0 72px;
    scroll-snap-align: start;
  }
}

/* 인원추가 모달 폼 */
.modal-form {
  margin: 10px 0;
}

.phone-input {
  width: 100%;
}

/* 출석 상태 표시 */
.attendance-status-chip {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
}

.attendance-status-chip.chip--warn {
  background: #ff9800; /* 미처리 - 주황색 */
}

.attendance-status-chip.chip--done {
  background: #4caf50; /* 출석 완료 - 초록색 */
}

.attendance-status-chip.chip--close {
  background: #f44336; /* 불참 처리 - 빨간색 */
}

/* R9 수업 관리 상자(수정·삭제·운동 프로그램 바로가기) */
.class-manage {
  flex-shrink: 0;
  padding: 12px;
  border-bottom: 1px solid rgba(114, 114, 114, 0.2);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.class-manage__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.class-manage__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}
.class-manage__date {
  font-size: 12px;
  color: #8e8e8e;
}
.class-manage__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.class-manage-btn {
  height: 32px;
  padding: 0 12px;
  border-radius: 5px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.class-manage-btn--danger {
  border-color: #7d2828;
  color: #ff817f;
}
.class-manage-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.class-manage__program {
  margin-left: auto;
  font-size: 13px;
  color: #78ff47;
  text-decoration: none;
}
.class-manage__hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: #8e8e8e;
}
.class-manage__msg {
  margin: 0;
  font-size: 13px;
  color: #78ff47;
}
.class-manage__msg--error {
  color: #ef4444;
}
.schedule-notice--error {
  color: #ef4444;
}

/* R9 수업 추가·수정 모달(기존 모달은 높이가 고정이라 따로 둔다) */
.class-form-modal {
  height: auto;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
}
.class-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.class-form__field {
  display: grid;
  grid-template-columns: 48px 1fr;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.class-form__times {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.class-form__label {
  font-size: 14px;
  color: #c8c8c8;
}
.class-form__input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px;
  background: #2a2a2a;
  border: 1px solid #555;
  color: #fff;
  border-radius: 8px;
  font-size: 14px;
  color-scheme: dark;
}
.class-form__input--narrow {
  max-width: 120px;
}
.class-form__input:disabled {
  opacity: 0.6;
}
.class-form__hint {
  margin: 0;
  font-size: 13px;
  color: #8e8e8e;
}
/* 이름 칸 아래 안내(라벨 칸 48px + 간격 8px만큼 들여 입력 칸에 맞춘다) */
.class-form__sub {
  margin: -6px 0 0 56px;
  font-size: 12px;
  color: #8e8e8e;
}
.class-form__error {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #ef4444;
}
@media (max-width: 520px) {
  .class-form__times {
    grid-template-columns: 1fr;
  }
}

/* 수업 정보 */
.class-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.capacity {
  font-size: 14px;
  color: #8e8e8e;
}

/* 예약자 목록 */
.member-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.member-name {
  font-weight: 600;
  color: #ffffff;
  font-size: 14px;
}

.member-name.clickable {
  cursor: pointer;
  transition: color 0.2s ease;
}

.member-name.clickable:hover {
  color: #78ff47;
}

.member-max-hr {
  font-size: 11px;
  color: #8e8e8e;
  margin-top: 4px;
}

/* 회원권·방문 배지 */
.member-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.badge {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
}

.badge-plain {
  background: #242627;
  color: #8e8e8e;
}

/* 재등록 대화를 꺼낼 때(만료 임박·잔여 소진)나 오래 비운 회원 */
.badge-warn {
  background: rgba(255, 149, 0, 0.15);
  color: #ff9500;
}

/* 회원권 없음·만료됨 */
.badge-danger {
  background: rgba(255, 59, 48, 0.15);
  color: #ff3b30;
}

/* 첫 수업 — 코치가 꼭 챙겨야 하는 회원 */
.badge-new {
  background: rgba(0, 129, 254, 0.18);
  color: #4da3ff;
}

/* 특이사항: 회원 상세(ProfileView)와 같은 노란 표시를 쓴다 */
.member-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 6px 0 0;
  padding: 6px 8px;
  border-left: 2px solid #fbbf24;
  background: rgba(251, 191, 36, 0.08);
  border-radius: 0 4px 4px 0;
  font-size: 12px;
  line-height: 1.5;
  color: #e8e8e8;
  /* 한국어는 낱말 중간에서 줄을 바꾸지 않는다, 띄어쓰기 없는 긴 글은 어디서든 끊는다 */
  white-space: pre-wrap;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.note-mark {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  font-size: 11px;
  font-weight: 700;
}

.attendance-status {
  font-size: 12px;
  color: #8e8e8e;
  margin-top: 2px;
}

.member-phone {
  font-size: 12px;
  color: #8e8e8e;
}

.booking-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.trial-record-link {
  color: #78ff47;
  border: 1px solid rgba(120, 255, 71, 0.55);
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

.trial-record-link:hover {
  background: rgba(120, 255, 71, 0.1);
}

.cancel-booking-btn {
  background: #ff4444;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.cancel-booking-btn:hover:not(:disabled) {
  background: #ff6666;
}

.cancel-booking-btn:disabled {
  background: #666;
  cursor: not-allowed;
  opacity: 0.5;
}

.attendance-btn {
  background: #78ff47;
  color: #000000;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-right: 8px;
}

.attendance-btn:hover {
  background: #6ce63a;
}

.action-btn {
  padding: 4px 8px;
  border-radius: 4px;
  border: none;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.attend-btn {
  background: #78ff47;
  color: #000000;
}

.attend-btn:hover {
  background: #6ce63a;
}

.absent-btn {
  background: #ff4757;
  color: #ffffff;
}

.absent-btn:hover {
  background: #ff3742;
}

.modal-btn-confirm:disabled {
  background: #3a3c3d;
  color: #8e8e8e;
  cursor: not-allowed;
}

.modal-footnote {
  margin: -4px 0 14px;
  color: #8e8e8e;
  font-size: 12px;
  line-height: 1.5;
}

.guest-tag {
  font-size: 12px;
  background: #78ff47;
  color: #000;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 6px;
  font-weight: 600;
}
.member-sub {
  font-size: 12px;
  color: #808080;
  margin-top: 2px;
}
.free-trial-btn {
  background: #2d1a1a;
  border: 1px solid #78ff47;
  color: #78ff47;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  margin-left: 8px;
}
.trial-link-note {
  margin: -4px 0 12px;
  color: #78ff47;
  font-size: 13px;
  line-height: 1.5;
}
.visit-kind {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.visit-kind-opt {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  border: 1px solid #555;
  border-radius: 8px;
  color: #ccc;
  font-size: 14px;
  cursor: pointer;
}
.visit-kind-opt input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.visit-kind-opt.active {
  border-color: #78ff47;
  color: #78ff47;
  font-weight: 600;
}
.visit-kind-opt:focus-within {
  outline: 2px solid #78ff47;
  outline-offset: 2px;
}
.visit-note {
  margin: 6px 0 0;
  font-size: 13px;
  color: #78ff47;
}
.sms-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 14px;
  color: #ccc;
  cursor: pointer;
}
.sms-check input {
  width: 16px;
  height: 16px;
  accent-color: #78ff47;
}
.trial-select {
  width: 100%;
  padding: 10px;
  background: #2a2a2a;
  border: 1px solid #555;
  color: #fff;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 8px;
}

/* 주차 네비게이션 */
.week-navigation {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  justify-content: center;
}

.nav-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-btn:hover:not(:disabled) {
  background: #252728;
  border-color: #444;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.current-week {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  min-width: 80px;
  text-align: center;
}
</style>
